import { IconButton, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { AudioRecorder } from 'src/components/AudioRecorder';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { AIActionButtons } from 'src/modules/ai-conversation/components/AIInput/AIActionButtons/AIActionButtons';
import {
  StyledActions,
  StyledActionsContainer,
  StyledTextarea,
} from 'src/modules/ai-conversation/components/AIInput/styles';
import { AIInputStateControl } from 'src/modules/ai-conversation/components/AIInput/useAIInputState';
import { aiTranscription } from 'src/requests/ai';

type MessageInputProps = {
  sendMessage: (message: string) => Promise<void> | void;
  showAudioAnswer?: boolean;
  placeholder?: string;
  control: AIInputStateControl;
  hideLabels?: boolean;
  disableButtons?: boolean;
  status?: string;
  stop: () => void;
};

export const AIInput = ({
  sendMessage,
  showAudioAnswer = true,
  placeholder,
  control,
  hideLabels,
  disableButtons,
  stop,
}: MessageInputProps) => {
  const {
    inputValue,
    audioAnswer,
    isLoading,
    setIsLoading,
    setInputValue,
    toggleAudioAnswer,
  } = control();

  const [transcribingAudio, setTranscribingAudio] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const handleSubmit = () => {
    if (!inputValue.trim()) return;
    handleSendMessage(inputValue);
  };

  const handleSendMessage = (message: string) => {
    setIsLoading(true);
    sendMessage(message);
  };

  const onSendAudio = async (file: File) => {
    // if (disabled) return;
    await handleSendAudio(ConversationMessageContentType.AUDIO, file);
  };

  const startRecording = () => {
    setIsRecording(true);
  };

  const stopRecording = () => {
    setIsRecording(false);
  };

  const onSendAudioHandler = (audio: File) => {
    onSendAudio(audio);
    stopRecording();
  };

  const handleSendAudio = async (type: string, audio: File) => {
    if (type !== ConversationMessageContentType.AUDIO) return;
    setTranscribingAudio(true);
    try {
      const { text } = await aiTranscription(audio);
      if (!text) return;
      handleSendMessage(text);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(
        translate('shared.snackbar.failed_to_transcribe_your_audio'),
        {
          variant: 'error',
        },
      );
    } finally {
      setTranscribingAudio(false);
    }
  };

  return (
    <>
      <StyledTextarea
        value={inputValue}
        disabled={isRecording}
        placeholder={
          placeholder || translate('copera_ai.input.type_your_message_here')
        }
        minRows={2}
        maxRows={12}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }
        }}
        autoFocus
      />
      <StyledActionsContainer>
        {/* LEFT ACTIONS */}
        <AIActionButtons
          control={control}
          disable={disableButtons}
          hideLabels={hideLabels}
          // hideLabels={hideLabels || !!contextFiles.length}
        />

        {/* RIGHT ACTIONS */}
        <StyledActions>
          <>
            {showAudioAnswer && (
              <Tooltip
                title={
                  audioAnswer
                    ? translate('copera_ai.input.disable_respond_with_audio')
                    : translate('copera_ai.input.enable_respond_with_audio')
                }
              >
                <IconButton
                  onClick={() => toggleAudioAnswer()}
                  color={audioAnswer ? 'primary' : 'inherit'}
                >
                  <Iconify
                    icon={
                      audioAnswer
                        ? 'fluent:sound-wave-circle-sparkle-24-filled'
                        : 'fluent:sound-wave-circle-sparkle-24-regular'
                    }
                    width={24}
                    height={24}
                  />
                </IconButton>
              </Tooltip>
            )}

            {transcribingAudio ? (
              <IconButton>
                <Iconify
                  icon="eos-icons:loading"
                  color="text.primary"
                  width={24}
                  height={24}
                />
              </IconButton>
            ) : isLoading ? (
              <IconButton onClick={stop}>
                <Iconify
                  icon="tabler:player-stop-filled"
                  color="text.primary"
                  width={24}
                  height={24}
                />
              </IconButton>
            ) : (
              <>
                {inputValue.length ? (
                  <IconButton onClick={handleSubmit}>
                    <Iconify
                      icon="tabler:send-2"
                      color="text.primary"
                      width={24}
                      height={24}
                    />
                  </IconButton>
                ) : (
                  <>
                    {isRecording ? (
                      <AudioRecorder
                        onStop={stopRecording}
                        onSend={onSendAudioHandler}
                        autoRecord
                      />
                    ) : (
                      <>
                        <Tooltip
                          title={translate(
                            'copera_ai.input.send_an_audio_message',
                          )}
                        >
                          <IconButton onClick={startRecording}>
                            <Iconify
                              icon="tabler:microphone"
                              color="text.primary"
                              width={24}
                              height={24}
                            />
                          </IconButton>
                        </Tooltip>

                        {/*<Tooltip title={translate('copera_ai.input.talk_with_copera_ai')}>*/}
                        {/*  <IconButton>*/}
                        {/*    <Iconify icon="tabler:phone" />*/}
                        {/*  </IconButton>*/}
                        {/*</Tooltip>*/}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        </StyledActions>
      </StyledActionsContainer>
    </>
  );
};
